<template>
    <div class="seller-badge-list">
        <div class="dokan-store-setting-header-wrap">
            <h1 class="entry-title">
                {{ __('Badges', 'dokan') }}
            </h1>
            <div class="search-by-order">
                <search @searched="searchByBadgeName" :title="__( 'Search by badge name', 'dokan' )" />
            </div>
        </div>
        <p>
            {{ __('Vendors with a good selling history on our marketplace are identified by seller badges. These badges are visible on your shop front and serve as a sign of quality assurance for customers. They can also be used to verify your authenticity.', 'dokan') }}
        </p>
        <hr class="wp-header-end">
        <div id="seller_badge_list_table">
            <list-table
                :columns="columns"
                :rows="filtered_badges"
                :loading="loading"
                :action-column="actionColumn"
                :actions="[]"
                :show-cb="false"
                :not-found="notFound"
                :total-pages="totalPages"
                :total-items="totalItems"
                :per-page="perPage"
                :current-page="getCurrentPage"
                :bulk-actions="[]"
                :text="translatedText"
                @bulk:click=""
                @pagination="goToPage">

                <template slot="badge_logo" slot-scope="data">
                    <img :src="data.row.badge_logo" :alt="data.row.badge_name">
                    <strong class="info" :class="data.row.badge_name">
                        {{ data.row.badge_name }}
                        <i class="fas fa-check-circle" :class="parseInt(data.row.acquired_level_count) === parseInt(data.row.level_count) || isYearActiveEvent(data.row) ? 'badge-published' : 'badge-draft'" v-if="data.row.vendor_count"></i>
                    </strong>
                    <div class="badge_level_text" v-if="!isYearActiveEvent(data.row)">{{ sprintf( __('Level: %d of %d', 'dokan'), data.row.acquired_level_count, data.row.level_count ) }}</div>
                    <div class="badge_level_text" v-else>{{ sprintf( __('Level: %d', 'dokan'), data.row.acquired_level_count ) }}</div>
                </template>

                <template slot="badge_description" slot-scope="data">
                    <span :class="data.row.badge_name">{{ data.row.event.description }}</span>
                </template>

                <template slot="badge_view" slot-scope="data">
                    <button @click.prevent="viewBadgeDetails(data.row.id)" class="button-secondary"> {{ __('View', 'dokan') }}</button>
                </template>

                <template slot="filters">
                    <select v-model="filters.badge_status">
                        <option v-for="(value, index ) in badge_status" :value="index">{{value}}</option>
                    </select>
                </template>
            </list-table>
        </div>
        <BadgeDetails v-if="badge.id" is_frontend />
        <UnseenBadges v-if="unseen_badges.length" :badges="unseen_badges" />
    </div>
</template>

<script>
import {computed} from "vue";

const ListTable = dokan_get_lib('ListTable');
const Debounce = dokan_get_lib('debounce');
const Search = dokan_get_lib('Search');
import BadgeDetails from "../../components/BadgeDetails.vue";
import UnseenBadges from "../components/UnseenBadges.vue";
import '../../css/list-tables.css';
import mixin from "../../Mixin.js";
import verificationTypesMixin from "../../verificationTypesMixin.js";

const apiBase = '/seller-badge/';
const apiUnseenBase = '/seller-badge/vendor-unseen-badges';
const apiSetBadgeAsSeen = '/seller-badge/set-badge-as-seen';

export default {
    name: 'SellerBadgeList',

    mixins: [mixin, verificationTypesMixin],

    components: {
        ListTable,
        Debounce,
        Search,
        BadgeDetails,
        UnseenBadges,
    },

    data () {
        return {
            badge: {
                id: 0,
                ts: false
            },
            badges: [],
            filtered_badges: [],
            unseen_badges: [],
            loading: false,
            columns: {
                'badge_logo': {
                    label: this.__('Acquired Badge', 'dokan'),
                    sortable: false,
                },
                'badge_description': {
                    label: this.__('Description', 'dokan'),
                    sortable: false,
                },
                'badge_view': {
                    label: this.__('Action', 'dokan'),
                    sortable: false,
                },
            },
            actionColumn: 'badge_name',
            totalItems: 0,
            totalPages: 1,
            perPage: 15,
            currentPage: 1,
            order: 'ASC',
            orderBy: 'badge_id',
            notFound: this.__('No badges found.', 'dokan'),
            badge_status: {
                'all': this.__( 'All', 'dokan' ),
                'my_badges': this.__('My Badges', 'dokan'),
                'available_badges': this.__('Available Badges', 'dokan'),
            },
            counts: {
                all: 0,
                my_badges: 0,
                available_badges: 0,
            },
            filters: {
                badge_name: '',
                badge_status: 'all',
            },
            translatedText: {
                loading: this.__( 'Loading', 'dokan' ),
                select_bulk_action: this.__( 'Select bulk action', 'dokan' ),
                bulk_actions: this.__( 'Bulk Actions', 'dokan' ),
                items: this.__( 'items', 'dokan' ),
                apply: this.__( 'Apply', 'dokan' ),
            },
        }
    },

    provide() {
        // use function syntax so that we can access `this`
        return {
            getBadgeValue: computed(() => this.badge ),
            getUnseenBadges: computed( () => this.unseen_badges ),
            getVerificationTypes: computed( () => this.verificationTypes ),
            setUnseenBadges: this.setUnseenBadges,
            setBadgeAsSeen: this.setBadgeAsSeen,
        }
    },

    watch: {
        'filters.badge_status': function (val) {
            this.currentPage = 1;
            this.filtered_badges = this.getBadges();
        },
        'currentPage'() {
            this.filtered_badges = this.getBadges();
        },
    },

    computed: {
        getSortBy() {
            return this.orderBy;
        },
        getSortOrder() {
            return this.order;
        },
        getCurrentPage () {
            let page = this.currentPage || 1;
            return parseInt(page);
        },
    },

    created () {
        this.fetchAllBadges()
        this.fetchUnseenBadges()
    },

    methods: {
        // provide method
        setUnseenBadges( badges ) {
            this.unseen_badges = badges;
        },

        // provide method
        setBadgeAsSeen() {
            if ( ! this.unseen_badges.length ) {
                return;
            }
            // get badge ids
            const badge_ids = this.unseen_badges.map( (item) => {
                return item.id;
            } );

            if ( ! badge_ids.length ) {
                return;
            }

            const self = this;

            //api call to set badge status as unseen
            dokan.api.put(apiSetBadgeAsSeen).done( ( response, status, xhr ) => {
                self.unseen_badges = [];
            }).always( () => {

            } ).fail( ( jqXHR ) => {
                let message = dokan_handle_ajax_error( jqXHR );
                if ( message ) {
                    self.showErrorAlert( message );
                }
            } );
        },

        // search by product
        searchByBadgeName: Debounce(function (payload) {
            if ( ! payload.length ) {
                return this.filtered_badges = this.getBadges();
            }
            this.filters.badge_name = payload.toLowerCase();
            this.filtered_badges = this.getBadges().filter( (item) => {
                return item.badge_name.toLowerCase().includes( this.filters.badge_name );
            } );
        }, 300),

        getBadges() {
            if ( undefined === this.badges || ! this.badges.length ) {
                return [];
            }
            const self = this;

            const badges = this.badges.filter( (item) => {
                if ( 'my_badges' === self.filters.badge_status ) {
                    return parseInt( item.vendor_count ) > 0;
                }
                else if ( 'available_badges' === self.filters.badge_status ) {
                    return parseInt( item.vendor_count ) === 0;
                }
                return true;
            } );

            // set pagination param
            this.totalItems = badges?.length;
            this.totalPages = this.totalItems > 0 && this.perPage > 0 ? parseInt( this.totalItems / this.perPage ) : 1;

            let start = ( this.currentPage - 1 ) * this.perPage ;
            let end = start + this.perPage;

            return badges.slice( start, end );
        },

        fetchAllBadges() {
            this.loading = true;
            let self = this;
            let args = {
                per_page: -1,
                page: 1,
                orderby: this.getSortBy,
                order: this.getSortOrder,
                is_frontend: true,
            };

            dokan.api.get(apiBase, args ).done( ( response, status, xhr ) => {
                self.resetCounts();
                self.badges = response.sort( this.sortByAcquiredBadge );
                self.counts.all = self.badges?.length;
                self.badges?.map( (item) => {
                    if ( parseInt( item.vendor_count ) > 0 ) {
                        self.counts.my_badges++;
                    } else {
                        self.counts.available_badges++;
                    }
                } );
                self.filtered_badges = self.getBadges();
            }).always( () => {
                self.loading = false;
            } ).fail( ( jqXHR ) => {
                self.badges = [];
                self.resetCounts();
                let message = dokan_handle_ajax_error( jqXHR );
                if ( message ) {
                    self.showErrorAlert( message );
                }
            } );
        },

        fetchUnseenBadges() {
            let self = this;

            dokan.api.get( apiUnseenBase, { is_frontend: true, })
            .done( ( response, status, xhr ) => {
                self.setUnseenBadges( response );
            }).always( () => {

            } ).fail( ( jqXHR ) => {
                self.unseen_badges = [];
                let message = dokan_handle_ajax_error( jqXHR );
                if ( message ) {
                    self.showErrorAlert( message );
                }
            } );
        },

        updatePagination( total_pages = 0, total_items = 0 ) {
            this.totalPages = parseInt( total_pages );
            this.totalItems = parseInt( total_items );
        },

        resetCounts() {
            this.counts.all  = 0;
            this.counts.my_badges = 0;
            this.counts.available_badges = 0;
            this.totalPages = 0;
            this.totalItems = 0;
        },

        sortByAcquiredBadge( a, b ) {
            const value1 = parseInt( a.vendor_count );
            const value2 = parseInt( b.vendor_count );
            if ( value1 > value2 ) {
                return -1;
            }
            if ( value1 < value2 ) {
                return 1;
            }
            return 0;
        },

        viewBadgeDetails( badge_id ) {
            this.badge.id = badge_id;
            this.badge.ts = Math.random();
        },

        doSort( column, order ) {
            this.orderBy = column;
            this.order = order;
        },

        goToPage(page) {
            const page_now = parseInt( page );
            this.currentPage = ! isNaN( page_now ) ? page_now : 1;
        },
    },
}
</script>

<style lang="less" scoped>
.seller-badge-list {
    .dokan-store-setting-header-wrap {
        display: flex;
        .entry-title {
            flex: 1;
        }
    }
    .button-secondary {
        font-size: 12px;
        min-width: 58px;
        border: 1px solid #1A9ED4;
        color: #1A9ED4;
        background-color: transparent;
        padding: 7px 15px 7px 15px;
    }

    #seller_badge_list_table {
        .badge_name {
            width: 30%;
        }

        .badge_view {
            .view_badge_icon:hover {
                color: #00a0d2;
                cursor: pointer;
            }
        }

        td img {
            float: left;
            margin-right: 10px;
            margin-top: 1px;
            width: 60px;
            height: auto;
        }

        .badge_logo {
            img {
                width: 41px;
            }
            .info {
                font-size: 14px;

                .fa-check-circle {
                    font-size: 0.875rem;
                    margin-left: 3px;

                    &.badge-published {
                        color: #139F84;
                    }

                    &.badge-draft {
                        color: #c8c8c8;
                    }
                }
            }
            .badge_level_text {
                font-size: 13px;
            }
        }

        .badge_description {
            font-size: 13px;
        }
    }
}
</style>
